import { useState, useCallback, useEffect } from 'react';
import {
  getDisplayAnswer,
  getDisplayQuestion,
} from 'src/components/Faqs/types';

export interface FaqFormData {
  id?: string;
  question?: string;
  answer?: string;
  issue?: string;
  image?: any;
  uploadType?: string;
  Embed?: string;
  linkTitle?: string;
  caption?: string;
  type?: string | string[];
  [key: string]: any;
}

type ValidationStatus = 'idle' | 'success' | 'error';
type SaveAction = 'SaveAndClose' | 'SaveAndAdd';

interface UseFaqFormConfig {
  validationStatus?: ValidationStatus;
  lastSaveAction?: SaveAction | null;
  onValidationSuccess?: (action: SaveAction) => void;
}

export interface UseFaqFormReturn {
  formData: FaqFormData;
  setFormData: (data: Partial<FaqFormData>) => void;
  setFormDataForEdit: (faq: any) => void;
  clearFormData: () => void;
  isEditing: boolean;
  showOverlay: boolean;
  openForm: () => void;
  closeForm: () => void;
  openFormForEdit: (faq: any) => void;
}

const initialFormData: FaqFormData = {};

const normalizeForForm = (faq: any): FaqFormData => {
  if (!faq) return {};

  return {
    ...faq,
    question: getDisplayQuestion(faq),
    answer: getDisplayAnswer(faq),
  };
};

const useFaqForm = (config: UseFaqFormConfig = {}): UseFaqFormReturn => {
  const { validationStatus, lastSaveAction, onValidationSuccess } = config;

  const [formData, setFormDataState] = useState<FaqFormData>(initialFormData);
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    if (validationStatus === 'success' && lastSaveAction) {
      onValidationSuccess?.(lastSaveAction);

      if (lastSaveAction === 'SaveAndClose') {
        setShowOverlay(false);
        setFormDataState(initialFormData);
      } else if (lastSaveAction === 'SaveAndAdd') {
        setFormDataState(initialFormData);
      }
    }
  }, [validationStatus, lastSaveAction, onValidationSuccess]);

  const setFormData = useCallback((data: Partial<FaqFormData>) => {
    setFormDataState((prev) => ({ ...prev, ...data }));
  }, []);

  const setFormDataForEdit = useCallback((faq: any) => {
    const normalized = normalizeForForm(faq);
    setFormDataState(normalized);
  }, []);

  const clearFormData = useCallback(() => {
    setFormDataState(initialFormData);
  }, []);

  const openForm = useCallback(() => {
    setShowOverlay(true);
  }, []);

  const closeForm = useCallback(() => {
    setShowOverlay(false);
    setFormDataState(initialFormData);
  }, []);

  const openFormForEdit = useCallback((faq: any) => {
    const normalized: FaqFormData = normalizeForForm(faq);

    setFormDataState(normalized);
    setShowOverlay(true);
  }, []);

  const isEditing: boolean = Boolean(formData.id);

  return {
    formData,
    setFormData,
    setFormDataForEdit,
    clearFormData,
    isEditing,
    showOverlay,
    openForm,
    closeForm,
    openFormForEdit,
  };
};

export default useFaqForm;
